import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState } from 'react';
import { Typography, Box, Paper, Button, Link, Alert, TextField, } from '@weave-mui/material';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress, } from '@mui/material';
import { useQuery, useMutation } from '@tanstack/react-query';
import { api } from '../../api/apiClient';
import { queryKeys } from '../../query/queryClient';
const DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;
function isValidCalendarDate(value) {
    if (!DATE_REGEX.test(value))
        return false;
    const [year, month, day] = value.split('-').map(Number);
    const d = new Date(year, month - 1, day);
    return d.getFullYear() === year && d.getMonth() === month - 1 && d.getDate() === day;
}
const DbRefreshHistoryTab = () => {
    const [reportDate, setReportDate] = useState(() => new Date().toISOString().split('T')[0]);
    const [isReportDownloading, setIsReportDownloading] = useState(false);
    const [reportError, setReportError] = useState(null);
    const handleDownloadFailedReport = async () => {
        if (!isValidCalendarDate(reportDate)) {
            setReportError('Please choose a valid date (YYYY-MM-DD).');
            return;
        }
        setReportError(null);
        setIsReportDownloading(true);
        try {
            const { blob, filename } = await api.dataFix.downloadFailedRecordReport(reportDate);
            const objectUrl = URL.createObjectURL(blob);
            try {
                const a = document.createElement('a');
                a.href = objectUrl;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
            }
            finally {
                URL.revokeObjectURL(objectUrl);
            }
        }
        catch (err) {
            const apiErr = err;
            setReportError(apiErr.message || 'Failed to download report.');
        }
        finally {
            setIsReportDownloading(false);
        }
    };
    const [currentToken, setCurrentToken] = useState(undefined);
    const [tokenStack, setTokenStack] = useState([]);
    const [failedReportRowErrors, setFailedReportRowErrors] = useState(() => new Map());
    const setFailedReportRowError = (snapshotTime, message) => {
        setFailedReportRowErrors((prev) => {
            const next = new Map(prev);
            if (message === null)
                next.delete(snapshotTime);
            else
                next.set(snapshotTime, message);
            return next;
        });
    };
    const handleDownloadFailedReportForRow = async (snapshotTime) => {
        setFailedReportRowError(snapshotTime, null);
        try {
            const { blob, filename } = await api.dataFix.downloadFailedRecordReport(snapshotTime);
            const objectUrl = URL.createObjectURL(blob);
            try {
                const a = document.createElement('a');
                a.href = objectUrl;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
            }
            finally {
                URL.revokeObjectURL(objectUrl);
            }
        }
        catch (err) {
            const apiErr = err;
            setFailedReportRowError(snapshotTime, apiErr?.message ?? 'Failed to download failed-record report.');
        }
    };
    const { data, isLoading, error } = useQuery({
        queryKey: queryKeys.dataFix.dbRefreshHistory(currentToken),
        queryFn: () => api.dataFix.getDbRefreshHistory(currentToken),
    });
    const [createDate, setCreateDate] = useState(() => new Date().toISOString().split('T')[0]);
    const [athenaError, setAthenaError] = useState(null);
    const [athenaSuccess, setAthenaSuccess] = useState(null);
    const athenaMutation = useMutation({
        mutationFn: (dbRefreshTime) => api.dataFix.createFullAthenaQueryJob(dbRefreshTime),
        onSuccess: (created) => {
            setAthenaError(null);
            setAthenaSuccess(`Full Athena query job started (jobId: ${created.jobId}).`);
        },
        onError: (err) => {
            setAthenaSuccess(null);
            setAthenaError(err?.message ?? 'Failed to start full Athena query.');
        },
    });
    const rows = data?.history ?? [];
    const nextToken = data?.nextToken;
    const hasPrev = tokenStack.length > 0;
    const pageNumber = tokenStack.length + 1;
    const handleNext = () => {
        if (nextToken) {
            setTokenStack((prev) => [...prev, currentToken]);
            setCurrentToken(nextToken);
        }
    };
    const handlePrev = () => {
        const stack = [...tokenStack];
        const prevToken = stack.pop();
        setTokenStack(stack);
        setCurrentToken(prevToken);
    };
    return (_jsxs(Box, { sx: { mt: 2 }, children: [_jsxs(Paper, { sx: { p: 3, mb: 2 }, children: [_jsx(Typography, { variant: "h6", sx: { mb: 2 }, children: "Create Refresh Record" }), _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 4, mb: 2 }, children: [_jsx(Typography, { sx: { minWidth: 160, fontWeight: 500 }, children: "DB Refresh Date" }), _jsx(TextField, { type: "date", value: createDate, onChange: (e) => setCreateDate(e.target.value), size: "small", sx: { width: 200 }, inputProps: { 'aria-label': 'New refresh record date' } })] }), athenaError && (_jsx(Alert, { severity: "error", sx: { mb: 2 }, children: athenaError })), athenaSuccess && (_jsx(Alert, { severity: "success", sx: { mb: 2 }, children: athenaSuccess })), _jsx(Button, { variant: "contained", disabled: !isValidCalendarDate(createDate) || athenaMutation.isPending, onClick: () => {
                            setAthenaError(null);
                            setAthenaSuccess(null);
                            athenaMutation.mutate(createDate);
                        }, startIcon: athenaMutation.isPending ? _jsx(CircularProgress, { size: 16 }) : null, children: athenaMutation.isPending
                            ? 'Starting…'
                            : 'Full Athena Scan Query & Create Refresh Record' })] }), _jsxs(Paper, { sx: { p: 3, mb: 2 }, children: [_jsx(Typography, { variant: "h6", sx: { mb: 2 }, children: "Failed Record Report" }), _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 4, mb: 2 }, children: [_jsx(Typography, { sx: { minWidth: 160, fontWeight: 500 }, children: "DB Refresh Date" }), _jsx(TextField, { type: "date", value: reportDate, onChange: (e) => setReportDate(e.target.value), size: "small", sx: { width: 200 } })] }), reportError && (_jsx(Alert, { severity: "error", sx: { mb: 2 }, children: reportError })), _jsx(Button, { variant: "contained", onClick: handleDownloadFailedReport, disabled: isReportDownloading || !isValidCalendarDate(reportDate), startIcon: isReportDownloading ? _jsx(CircularProgress, { size: 16 }) : null, children: isReportDownloading ? 'Downloading...' : 'Download CSV' })] }), error && (_jsx(Paper, { sx: { p: 3, mb: 2 }, children: _jsxs(Typography, { color: "error", children: ["Failed to load DB refresh history: ", error.message] }) })), !error && (_jsxs(_Fragment, { children: [_jsx(Paper, { children: _jsx(TableContainer, { children: _jsxs(Table, { children: [_jsx(TableHead, { children: _jsxs(TableRow, { sx: { '& th': { fontWeight: 700 } }, children: [_jsx(TableCell, { children: "Refresh Date" }), _jsx(TableCell, { children: "Batch IDs" }), _jsx(TableCell, { children: "Failed Report" })] }) }), _jsxs(TableBody, { children: [isLoading && (_jsx(TableRow, { children: _jsx(TableCell, { colSpan: 3, align: "center", sx: { py: 4 }, children: _jsx(CircularProgress, { size: 24 }) }) })), !isLoading && rows.length === 0 && (_jsx(TableRow, { children: _jsx(TableCell, { colSpan: 3, align: "center", children: _jsx(Typography, { variant: "body2", color: "text.secondary", sx: { py: 3 }, children: "No DB refresh history found." }) }) })), !isLoading &&
                                                rows.map((entry, index) => {
                                                    return (_jsxs(TableRow, { hover: true, children: [_jsx(TableCell, { sx: { verticalAlign: 'top' }, children: _jsx(Typography, { variant: "body2", children: entry.snapshotTime }) }), _jsx(TableCell, { sx: {
                                                                    verticalAlign: 'top',
                                                                    whiteSpace: 'normal',
                                                                    wordBreak: 'break-word',
                                                                }, children: _jsx(Typography, { variant: "body2", fontFamily: "monospace", children: entry.batchIds?.join(', ') ?? '—' }) }), _jsx(TableCell, { sx: { verticalAlign: 'top' }, children: _jsxs(Box, { children: [_jsx(Link, { component: "button", variant: "body", onClick: () => void handleDownloadFailedReportForRow(entry.snapshotTime), children: "Download Failed Report" }), failedReportRowErrors.has(entry.snapshotTime) && (_jsx(Typography, { variant: "body2", color: "error", sx: { mt: 0.5 }, role: "alert", children: failedReportRowErrors.get(entry.snapshotTime) }))] }) })] }, `${entry.snapshotTime}-${index}`));
                                                })] })] }) }) }), !isLoading && (hasPrev || nextToken) && (_jsxs(Box, { sx: {
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            gap: 1,
                            mt: 1,
                        }, children: [_jsxs(Typography, { variant: "body2", color: "text.secondary", children: ["Page ", pageNumber] }), _jsx(Button, { variant: "outlined", size: "small", onClick: handlePrev, disabled: !hasPrev, children: "Previous" }), _jsx(Button, { variant: "outlined", size: "small", onClick: handleNext, disabled: !nextToken, children: "Next" })] }))] }))] }));
};
export default DbRefreshHistoryTab;
